import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Kanban board controller for /dashboard/job-tracker.
//
// Three responsibilities:
//  1. Drag-and-drop (SortableJS) across the five status columns. On a
//     cross-column drop it POSTs to the existing update_status endpoint and
//     lets the server's Turbo Stream reconcile the DOM + counts (server is
//     authoritative). On failure it reverts the optimistic move.
//  2. The per-card "move to status" menu (the ⋯ button). The board is a
//     horizontal scroller (overflow-x:auto forces overflow-y:auto), which
//     would clip an in-flow absolute menu — so the menu is shown as a
//     position:fixed popover anchored to the button, escaping all overflow.
//  3. The collapsed `closed` archive rail: a click toggle (toggleRail) plus an
//     automatic open for the duration of a drag, because the collapsed rail's
//     list is display:none and therefore not a drop target at all.
export default class extends Controller {
  static targets = ["list"]

  connect() {
    this.sortables = this.listTargets.map((list) =>
      Sortable.create(list, {
        group: "job-tracker",
        animation: 150,
        // Use the pointer-based fallback rather than native HTML5 DnD: native
        // drag is flaky across browsers and breaks inside the board's
        // overflow-x-auto scroll container. fallbackOnBody renders the drag
        // clone on <body> so it isn't clipped by that overflow.
        forceFallback: true,
        fallbackOnBody: true,
        fallbackTolerance: 4,
        ghostClass: "kanban-ghost",
        chosenClass: "kanban-chosen",
        dragClass: "kanban-drag",
        filter: "[data-no-drag], a, button",
        preventOnFilter: false,
        onStart: this.onStart.bind(this),
        onEnd: this.onEnd.bind(this),
      })
    )
    // The .jt-scroll this drag opened the archive rail on, or null. Doubles as
    // the "was it auto-opened" flag — see onStart.
    this.autoOpenedRail = null
    this._onDocClick = this.onDocumentClick.bind(this)
    this._onScroll = () => this.closeAllMenus()
    this._onKey = (e) => {
      if (e.key === "Escape") this.closeAllMenus()
    }
    document.addEventListener("click", this._onDocClick)
    document.addEventListener("scroll", this._onScroll, true)
    document.addEventListener("keydown", this._onKey)
  }

  disconnect() {
    // A drag still in flight when Turbo swaps the board never reaches onEnd,
    // and the flag lives on .jt-scroll — an ANCESTOR that outlives the swap —
    // so an auto-opened rail would be stranded open with no drag to close it.
    this.closeAutoOpenedRail()
    this.sortables?.forEach((s) => s.destroy())
    document.removeEventListener("click", this._onDocClick)
    document.removeEventListener("scroll", this._onScroll, true)
    document.removeEventListener("keydown", this._onKey)
  }

  // Collapsed, `.jt-col--rail .jt-col__body` is display:none — so the `closed`
  // list has no box on screen, SortableJS has nothing to hit-test, and a card
  // cannot be archived by drag at all: the user had to stop and open the rail
  // first. Opening it for the duration of the drag is what makes the archive
  // reachable, and the rail is the drag's to close again only if the drag is
  // what opened it.
  //
  // A rail that is ALREADY open was opened by the user via toggleRail — it is
  // PINNED, and the drag must leave it exactly as it found it. Every path here
  // writes this.autoOpenedRail, so the flag cannot survive stale into a later
  // drag even if some drag never reaches onEnd.
  onStart() {
    const scroll = this.element.closest(".jt-scroll")
    if (!scroll || scroll.classList.contains("is-rail-open")) {
      this.autoOpenedRail = null // pinned (or no scroller): not ours to close
      return
    }
    scroll.classList.add("is-rail-open")
    this.autoOpenedRail = scroll
    this.syncRailToggle(true)
  }

  // Holding the element rather than a boolean means cleanup still works from
  // disconnect(), where this.element may already be detached and closest()
  // would no longer find the scroller.
  closeAutoOpenedRail() {
    const scroll = this.autoOpenedRail
    if (!scroll) return
    this.autoOpenedRail = null
    scroll.classList.remove("is-rail-open")
    this.syncRailToggle(false)
  }

  // aria-expanded/aria-label live on the toggle BUTTON while the state class
  // lives on .jt-scroll, so every writer of that class has to write the button
  // too. Without this, one auto-open/auto-close cycle leaves the button
  // permanently claiming "Collapse closed archive" over a 60px rail.
  syncRailToggle(expanded) {
    const btn = this.element.querySelector("[data-rail-toggle]")
    if (!btn) return
    btn.setAttribute("aria-expanded", expanded ? "true" : "false")
    btn.setAttribute("aria-label", expanded ? "Collapse closed archive" : "Expand closed archive")
  }

  async onEnd(event) {
    // FIRST, before the await below: this method is async, so anything after
    // the fetch would hold the rail open for the whole server round-trip.
    this.closeAutoOpenedRail()

    const { from, to, item } = event
    if (from === to) return // no within-column ordering persistence
    const status = to.dataset.status
    const url = item.dataset.updateUrl
    if (!status || !url) return
    try {
      const resp = await fetch(`${url}?status=${encodeURIComponent(status)}`, {
        method: "POST",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": this.csrfToken(),
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
      })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      const html = await resp.text()
      if (window.Turbo) window.Turbo.renderStreamMessage(html)
    } catch (err) {
      // Revert the optimistic move back to its original column/position.
      const ref = from.children[event.oldIndex] || null
      from.insertBefore(item, ref)
      console.error("[kanban] move failed, reverted:", err)
    }
  }

  toggleMenu(event) {
    event.preventDefault()
    event.stopPropagation()
    const button = event.currentTarget
    const menu = button.parentElement.querySelector("[data-kanban-menu]")
    if (!menu) return
    const willOpen = menu.classList.contains("hidden")
    this.closeAllMenus()
    if (!willOpen) return

    menu.classList.remove("hidden")
    menu.style.position = "fixed"
    menu.style.visibility = "hidden"
    const r = button.getBoundingClientRect()
    const mh = menu.offsetHeight
    const mw = menu.offsetWidth
    let top = r.bottom + 4
    if (top + mh > window.innerHeight - 8) top = Math.max(8, r.top - mh - 4)
    let left = r.right - mw
    if (left < 8) left = 8
    menu.style.top = `${top}px`
    menu.style.left = `${left}px`
    menu.style.visibility = ""
  }

  // The `closed` column ships as a rail because it is a terminal archive nobody
  // works out of, and five equal columns did not fit 1440px. Expanding it is a
  // class flip, not a width written in JS — the widths all live in
  // app.tailwind.css, so there is one source of truth for the board's geometry.
  //
  // The flag goes on .jt-scroll, NOT on the column, because that is the only
  // element the stylesheet can read it from:
  //   · `.jt-scroll.is-rail-open{--rail-w:232px}` is a COMPOUND selector — the
  //     track width only widens if .jt-scroll itself carries the class.
  //   · `.is-rail-open .jt-plate--closed` restyles the funnel plate, which sits
  //     in .jt-spine — a SIBLING of this controller's element, not a descendant.
  // A class on the column (or on this.element) reaches neither. .jt-scroll is
  // the nearest common ancestor of the board and the spine, so it is the one
  // place a single flag can drive both halves of the geometry.
  // A rail opened HERE is pinned: onStart sees it already open and leaves it
  // alone, so a drag can no longer collapse the user's own choice.
  toggleRail() {
    const scroll = this.element.closest(".jt-scroll")
    if (!scroll) return

    const expanded = scroll.classList.toggle("is-rail-open")
    this.syncRailToggle(expanded)

    // Any open card popover is position:fixed and was placed against the old
    // geometry, so it would now float away from its button.
    this.closeAllMenus()
  }

  closeAllMenus() {
    this.element.querySelectorAll("[data-kanban-menu]:not(.hidden)").forEach((m) => {
      m.classList.add("hidden")
      m.style.position = ""
      m.style.top = ""
      m.style.left = ""
      m.style.visibility = ""
    })
  }

  onDocumentClick(event) {
    if (event.target.closest("[data-kanban-menu]")) return
    if (event.target.closest('[data-action~="kanban#toggleMenu"]')) return
    this.closeAllMenus()
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
